import PrimeReact from "primereact/api";
import { Tooltip } from 'primereact/tooltip';
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Menubar } from "primereact/menubar";
import { Panel } from 'primereact/panel';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from "primereact/toast";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { classNames } from "primereact/utils";
import { SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { InterviewNode, QuestionType } from "../../../../CandidatePortal/scenes/Interview/Chatbot/interface";
import useUnload from "../../../../constants/helpers/useUnload";
import ExistingFlowDialog from "../components/Dialogs/ExistingFlowDialog";
import FlowTemplateDialog from "../components/Dialogs/FlowTemplateDialog";
import InterviewNodeForm from "../components/InterviewNodeForm";
import InterviewReactFlow from "../components/InterviewReactFlow";
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch } from "../../../../store";
import { toggleChangesMade, updateInterviewNodeList, deleteInterviewNode, updateNodeInNodeList, 
         setInterviewFlowEnum, addNodesToNodeList, setInterviewFlow} from './InterviewFlowSlice'
import * as interviewFlowAction from "./InterviewFlowAction"
import {
  AutoLayoutState, FlowType, ImportNodeStatus, InterviewFlow,
  InterviewFlowGetStatus, InterviewFlowUpdateStatus, InterviewNodeCreateStatus,
  InterviewNodeDeleteStatus, InterviewNodeListStatus, InterviewNodeUpdateStatus
} from "../interface"
import { typesOfId } from "./enums"
import GenerateFlowDialog from "../components/Dialogs/GenerateFlowDialog";

const InterviewSetup = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { loading, error, interviewFlow, changesMade, interviewNodeList, 
          existingInterviewFlows, interviewFlowGlobalTemplateListing, interviewFlowGlobalTemplateTotalRecords } 
          = useSelector((state: any) => state.interviewFlow)

  const [displaySideBar, setDisplaySideBar] = useState<boolean>(false);
  const [intentNode, setIntentNode] = useState<InterviewNode>({} as InterviewNode);
  const [isUpdate, setIsUpdate] = useState<boolean>();
  const [autosave, setAutosave] = useState<boolean>(false);
  const [autolayout, setAutolayout] = useState<boolean>(false);
  const [autoLayoutState, setAutoLayoutState] = useState<AutoLayoutState>(AutoLayoutState.NEUTRAL);
  const [interviewFlowName, setInterviewFlowName] = useState<string>("");
  const [interpretedState, setInterpretedState] = useState<InterpretedSetupAction>();
  const { interviewFlowId, interviewId, jobId } = useParams<{
    interviewFlowId: string,
    interviewId: string, jobId: string
  }>();
  const [displayFlowTemplateDialog, setDisplayFlowTemplateDialog] = useState<boolean>(false);
  const [displayExistingFlowDialog, setDisplayExistingFlowDialog] = useState<boolean>(false);
  const [displayCreateCustomSlotDialog, setDisplayCreateCustomSlotDialog] = useState<boolean>(false);
  const [displayGenerateFlowDialog, setDisplayGenerateFlowDialog] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<String>("")
  const [firstNode, setFirstNode] = useState<InterviewNode>({} as InterviewNode)
  const [lastNode, setLastNode] = useState<InterviewNode>({} as InterviewNode)

  const history = useHistory();
  interface dispatchGetInterviewFlowPayload {
    id: number | string
    typeOfId: string
  }

  enum InterpretedSetupAction {
    //To indicate whether the save action is for creating or updating existing flow
    CREATE, UPDATE
  }

  PrimeReact.ripple = true
  const toast = useRef<any>();
  const saveTimeoutID = useRef<NodeJS.Timeout>();
  const flowRef = useRef<any>();

  useEffect(() => {
    init();
  }, [])

  const init = () => {
    //Handle creating/updating interviewFlow for interviewId
    let payload: dispatchGetInterviewFlowPayload = { id: "", typeOfId: "" }
    if (interviewId)
      payload = { id: interviewId, typeOfId: typesOfId[typesOfId.InterviewId] }
    //Handle creating/updating interviewFlow for jobId
    else if (jobId)
      payload = { id: jobId, typeOfId: typesOfId[typesOfId.JobId] }
    //Handle creating/updating interviewFlow for global
    else if (interviewFlowId)
      payload = { id: interviewFlowId, typeOfId: typesOfId[typesOfId.Id] }

    dispatch(interviewFlowAction.getInterviewFlow(payload))
      .unwrap()
      .then(() => {
        // successs
        toast.current.show({
          severity: 'success',
          summary: 'Interview Flow retrieved',
          detail: `Interview Flow has been loaded successfully`,
          life: 3000
        })

        // reset node
        resetNodeView()
      })
      .catch((error: any) => {
        // if interview flow not found, dont show error
        if (error.indexOf('not found') === -1) {
          toast.current.show({
            severity: 'warn',
            summary: 'Interview Flow Error',
            detail: `Interview flow failed to load.`,
            life: 3000
          })
        }
      })
      .finally(() => {
        // set interview flow type
        if (interviewId) { //If interviewId exist in url params, interpreted as creating flow for interview
          dispatch(setInterviewFlow({interviewFlowEnum: FlowType.INTERVIEW, interviewId: interviewId}))
        }
        else if (jobId) { //If jobId exist in url params, interpreted as creating flow for job
          dispatch(setInterviewFlow({interviewFlowEnum: FlowType.JOB, jobId: Number(jobId)}))
        }
        else //No id exist in url params, create template flow
          dispatch(setInterviewFlow({interviewFlowEnum: FlowType.GLOBAL}))
      })
  }

  //first and last node tracking
  const findAndSetFirstAndLastNodes = () => {
    setFirstNode({} as InterviewNode)
    setLastNode({} as InterviewNode)
    if (!(interviewFlow === undefined || interviewFlow === null)) {
      if (!(interviewFlow.interviewNodeList === undefined || interviewFlow.interviewNodeList === null))
        interviewFlow.interviewNodeList.map((node: any) => {
          if (node.isFirstQuestion)
            setFirstNode(node)
          else if (node.isLastQuestion)
            setLastNode(node)
        })
    }
  }

  const resetNodeView = () => {
    setDisplaySideBar(false)
    findAndSetFirstAndLastNodes()
    setTimeout(() => {
      flowRef.current.fitView()
    }, 100)
  }

  useEffect(() => {
    if (interviewFlow) {
      // setup flow state 
      setInterpretedState(interviewFlow.id === undefined ? InterpretedSetupAction.CREATE : InterpretedSetupAction.UPDATE)

      // populate name into textbox
      setInterviewFlowName(interviewFlow.name || '')
    }
  }, [interviewFlow])

  useEffect(() => {
    setDisplaySideBar(false)
  }, [interviewNodeList])

  useEffect(() => {
      if (error)
      toast.current.show({
        severity: 'error',
        summary: "Save Failed",
        detail: error,
        life: 3000,
      })
  }, [error])

  const saveInterviewFlow = async (timeout: boolean) => {
    console.log("Saving interview flow...:", interviewFlow);

    const triggerCreate = () => {
      dispatch(interviewFlowAction.createAndSaveInterviewFlow(interviewFlow))
        .unwrap()
        .then(() => {
          // reset nodes
          resetNodeView();
        })
        .catch((error: any) => {
          toast.current.show({
            severity: 'error',
            summary: "Save Failed",
            detail: error,
            life: 3000,
          })
        })
    }
    const triggerUpdate = () => {
      dispatch(interviewFlowAction.updateInterviewFlow(interviewFlow))
        .unwrap()
        .then(() => {
          // reset nodes
          resetNodeView()
        })
        .catch((error: any) => {
          toast.current.show({
            severity: 'error',
            summary: "Save Failed",
            detail: error,
            life: 3000,
          })
        })
    }

    await new Promise<void>(async resolve => {
      if (interviewFlow.name === undefined || interviewFlow.name.length === 0)
        toast.current.show({
          severity: 'error',
          summary: "Save Failed",
          detail: "Please provide a name for the interview flow.",
          life: 3000,
          id: 'saveError'
        })
      else {
        if (timeout && saveTimeoutID.current)
          clearTimeout(saveTimeoutID.current);

        if (interpretedState === InterpretedSetupAction.CREATE) {
          if (timeout)
            saveTimeoutID.current = setTimeout(() => {
              triggerCreate()
            }, 1000)
          else {
            triggerCreate()
          }
        }
        else {
          if (timeout)
            saveTimeoutID.current = setTimeout(() => {
              triggerUpdate()
            }, 1000)
          else {
            triggerUpdate()
          }
        }
      }
      resolve()
    })
  }

  //Update interviewNodeList within interviewFlow whenever layout for nodeList is done
  useEffect(() => {
    if (autoLayoutState !== AutoLayoutState.NEUTRAL && interviewNodeList.length > 0) {
      updateMappedNodeListToInterviewNodeList(interviewNodeList)
      if (autosave)
        saveInterviewFlow(true)
      setAutoLayoutState(AutoLayoutState.NEUTRAL)
    }
  }, [autoLayoutState])

  const updateMappedNodeListToInterviewNodeList = async (nodeList: InterviewNode[]) => {
    await new Promise<void>(resolve => {
      let flowInterviewNodeList = nodeList.map((eachNode: InterviewNode) => {
        return {
          ...eachNode,
          interviewFlowId: interviewFlow.id
        }
      })

      dispatch(updateInterviewNodeList(flowInterviewNodeList))
      resolve()
    })
  }

  const saveMenuItem = {
    label: 'Save',
    icon: 'pi pi-save',
    command: () => saveInterviewFlow(false)
  }

  const menuItems = [
    {
      label: 'New Node',
      icon: 'pi pi-plus-circle',
      items: [
        {
          label: 'Question',
          items: [
            {
              label: 'ConfirmationQuestion',
              command: () => {
                setIntentNode({ typeOfQuestion: QuestionType.ConfirmationQuestion } as InterviewNode);
                setIsUpdate(false);
                setDisplaySideBar(true);
              }
            },
            {
              label: 'OpenEndedQuestion',
              command: () => {
                setIntentNode({ typeOfQuestion: QuestionType.OpenEndedQuestion } as InterviewNode);
                setIsUpdate(false);
                setDisplaySideBar(true);
              }
            },
            {
              label: 'ConditionalQuestion',
              command: () => {
                setIntentNode({ typeOfQuestion: QuestionType.ConditionalQuestion } as InterviewNode);
                setIsUpdate(false);
                setDisplaySideBar(true);
              }
            },
            {
              label: 'DelegatedQuestion',
              command: () => {
                setIntentNode({ typeOfQuestion: QuestionType.DelegatedQuestion } as InterviewNode);
                setIsUpdate(false);
                setDisplaySideBar(true);
              }
            }
          ]
        }
      ]
    },
    {
      label: 'Import',
      icon: 'pi pi-download',
      items: [
        {
          label: 'Flow Templates',
          command: () => setDisplayFlowTemplateDialog(true)
        },
        {
          label: 'From Existing Flows',
          command: () => setDisplayExistingFlowDialog(true)
        }
      ]
    },
    {
      label: 'Generate',
      icon: 'pi pi-bolt',
      command: () => setDisplayGenerateFlowDialog(true)
    },
    saveMenuItem
  ]

  const end = () => {
    return (
      <div className="save-changes-icon flex flex-wrap align-items-center">
        <Tooltip target=".first-node-warning-icon" />
        {(Object.keys(firstNode).length === 0 || Object.keys(lastNode).length === 0) &&
          <i className="first-node-warning-icon pi pi-info-circle"
            data-pr-tooltip={
              (Object.keys(firstNode).length === 0 ? "No first node is set. \n" : "") +
              (Object.keys(lastNode).length === 0 ? "No last node is set. \n" : "")}
            data-pr-position="bottom"
            style={{ paddingRight: '10px' }} />}
        <div style={{ paddingRight: '10px' }}>
          <Tooltip target=".save-state-icon" />
          {loading && autosave ?
            <i className="save-state-icon pi pi-spin pi-spinner"
              style={{ fontSize: '1rem' }} /> :
            errorMessage || error ?
              <i className="save-state-icon pi pi-times" data-pr-tooltip="Save failed" data-pr-position="bottom"
                style={{ color: 'red' }}></i> :
              interviewFlow !== null || interviewFlow !== undefined ?
                changesMade ? <i className="save-state-icon pi pi-replay"
                  data-pr-tooltip="Changes pending save" data-pr-position="bottom" style={{ color: 'orange' }}></i> :
                  <i className="save-state-icon pi pi-check-circle" data-pr-tooltip="Saved"
                    data-pr-position="bottom" style={{ color: 'green' }}></i> :
                <div></div>}
        </div>
        <Checkbox inputId="autosaveCheckbox" checked={autosave} onChange={e => setAutosave(e.checked || false)} tooltip="Saves on every node update" tooltipOptions={{ position: 'bottom' }}></Checkbox>
        <label htmlFor="autosaveCheckbox" className="ml-2 mr-2 p-checkbox-label">Autosave</label>
        <Checkbox inputId="autolayoutCheckbox" checked={autolayout} onChange={e => setAutolayout(e.checked || false)} tooltip="Layout on every node update" tooltipOptions={{ position: 'bottom' }}></Checkbox>
        <label htmlFor="autolayoutCheckbox" className="ml-2 p-checkbox-label">Autolayout</label>
      </div>
    )
  }

  const handleNodeClick = useCallback((nodeClicked: { data: { label: string; }; }) => {
    let selectedNode = interviewNodeList.find((node: InterviewNode) => node.name === nodeClicked.data.label);
    if (selectedNode && Object.keys(selectedNode).length !== 0) {
      setIntentNode(selectedNode);
      setIsUpdate(true);
      setDisplaySideBar(true);
    } else {
      toast.current.show({ severity: 'error', summary: 'Error getting node details', detail: "The node that you have clicked may have been corrupted, please reload the page.", life: 3000 });
    }
  }, [interviewNodeList])

  const handleAutoLayoutState = useCallback((layoutState: SetStateAction<AutoLayoutState>) => {
    setAutoLayoutState(layoutState);
  }, [])

  const updateInterviewFlowName = (e: any) => {
    setInterviewFlowName(e.target.value);
    dispatch(setInterviewFlow({name: e.target.value}));
    dispatch(toggleChangesMade());
  }

  const start = () => {
    return (
      <IconField iconPosition="left">
        <InputIcon className="pi pi-pencil"> </InputIcon>
        <InputText
          required
          className={classNames({ 'p-invalid': !interviewFlowName })}
          id="interviewFlowName"
          placeholder="Enter name"
          autoFocus
          value={interviewFlowName}
          onChange={updateInterviewFlowName}
        ></InputText>
      </IconField>
    )
  }

  const updateSetChangesMade = useCallback((changesMade: boolean | ((prevState: boolean) => boolean)) => {
  }, [])

  useUnload((e: any) => {
    e.preventDefault();
    if (changesMade)
      e.returnValue = "You have unsaved changes, are you sure you want to leave this page?"
  });

  return (
    <div>
      <Toast ref={toast} />
      <Prompt when={changesMade} message="You have unsaved changes. Are you sure you want to leave?"></Prompt>
      <FlowTemplateDialog
        interviewFlowGlobalTemplateListing={interviewFlowGlobalTemplateListing}
        interviewFlowGlobalTemplateTotalRecords={interviewFlowGlobalTemplateTotalRecords}
        loading={loading}
        getInterviewFlowGlobalTemplateListing={interviewFlowAction.getInterviewFlowGlobalTemplateListing}
        displayFlowTemplateDialog={displayFlowTemplateDialog}
        importInterviewFlowByInterviewFlowId={interviewFlowAction.getInterviewFlow}
        onHide={() => setDisplayFlowTemplateDialog(false)} ></FlowTemplateDialog>
      <ExistingFlowDialog
        interviewFlow={interviewFlow}
        existingFlowDialogListing={existingInterviewFlows}
        loading={loading}
        getExistingFlowsForTenant={interviewFlowAction.getExistingFlowsForTenant}
        getInterviewNodesFor={interviewFlowAction.getInterviewNodesByFlow}
        displayExistingFlowDialog={displayExistingFlowDialog}
        importInterviewFlowByInterviewFlowId={interviewFlowAction.getInterviewFlow}
        importNodes={addNodesToNodeList}
        onHide={() => setDisplayExistingFlowDialog(false)}></ExistingFlowDialog>
      <GenerateFlowDialog
        displayGenerateFlowDialog={displayGenerateFlowDialog}
        retrieveInterviewFlow={interviewFlowAction.getInterviewFlow}
        onHide={() => setDisplayGenerateFlowDialog(false)}></GenerateFlowDialog>
      <Menubar model={interviewFlow.id === undefined ? [saveMenuItem] : menuItems} start={start} end={end} />
      <Sidebar className="p-sidebar-md" visible={displaySideBar} onHide={() => setDisplaySideBar(false)}>
        <InterviewNodeForm
          //actionOnSubmit={isUpdate ? updateInterviewNodeToNodeList : addInterviewNodeToNodeList}
          actionOnSubmit={updateNodeInNodeList}
          actionOnDelete={deleteInterviewNode}
          isUpdate={isUpdate}
          intentNode={intentNode}
          firstNode={firstNode}
          lastNode={lastNode}
          intentNameList={interviewNodeList.map((node: InterviewNode) => node.name)}
          autosave={autosave} />
      </Sidebar>
      <Panel>
        {interviewFlow?.id !== undefined ? (
          <InterviewReactFlow
            nodeList={interviewNodeList}
            setChangesMade={toggleChangesMade}
            handleNodeClick={handleNodeClick}
            handleAutoLayoutState={handleAutoLayoutState}
            updateInterviewNodeList={updateInterviewNodeList}
            autolayout={autolayout}
            ref={flowRef} />
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px', // Adjust the height as needed
            fontSize: '1.2em', // Adjust the font size as needed
            color: 'grey',
            padding: '20px'
          }}>
            Please enter a name and save to proceed.
          </div>
        )}
      </Panel>
    </div>
  )
}

export default InterviewSetup